'use client'

import Autoplay from 'embla-carousel-autoplay'

// Components
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { HomeProjectCard } from '@/app/[lang]/(home)/_components/projects/project-card'
import { GalleryModal } from '@/components/modals/gallery-modal'

// Types
import type { Project } from '@/lib/generated/prisma/client'
import type { Locale } from '@/i18n-config'
import type { Dictionary } from '@/types/global'
import Image from 'next/image'
import BlurImage from '@/components/shared/blur-image'
import { cn, placeholderBlurhash } from '@/lib/utils'
import { useState } from 'react'
import { useMediaQuery } from '@/hooks/use-media-query'

export function ProjectCarousel({ project }: { project: Project }) {
  const { width } = useMediaQuery()
  // State to control the modal and the current image index
  const [modalOpen, setModalOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const openModal = (index: number) => {
    setCurrentIndex(index)
    setModalOpen(true)
  }
  // Combine coverImage with images array, filtering out null/empty values
  const carouselImages = [project.coverImage, ...(project.images || [])].filter(
    (img): img is string => Boolean(img)
  )

  return (
    <>
      <Carousel
        plugins={[
          Autoplay({
            delay: 4000,
          }),
        ]}
        className="w-full"
      >
        <CarouselContent className="-ml-0">
          {carouselImages.map((image, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'pl-0',
                width && width > 1024 ? 'cursor-pointer' : ''
              )}
              onClick={() => {
                width && width > 1024 ? openModal(index) : null
              }}
            >
              <div className="relative aspect-video">
                <Image
                  src={image || ''}
                  blurDataURL={placeholderBlurhash}
                  width={1200}
                  height={600}
                  priority={index === 0}
                  placeholder="blur"
                  className="size-full border-b object-cover object-center md:rounded-t-xl"
                  sizes="(max-width: 768px) 770px, 1000px"
                  alt={project.title}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="" />
        <CarouselNext className="" />
      </Carousel>
      <GalleryModal
        images={carouselImages}
        title={project.title}
        description={project.description}
        modalOpen={modalOpen}
        setModalOpen={() => setModalOpen(false)}
        initialIndex={currentIndex}
      />
    </>
  )
}
